import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import FadeIn from "@/components/FadeIn";
import SEO from "@/components/SEO";
import axios from "axios";
import { Calendar, ArrowRight, User, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog`).then((res) => setPosts(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page">
      <SEO title="Blog" description="Expert articles on medical tourism, patient guides, treatment insights, Ayurveda wellness, and Kerala healthcare." path="/blog" keywords="medical tourism blog, healthcare articles India, Ayurveda blog, patient stories medical tourism" />
      <section className="section-padding bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <span className="overline">BLOG</span>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-light text-[#03045e] mt-3 mb-6 tracking-tight">
              Medical Tourism <span className="font-medium text-[#00A4A9]">Insights</span>
            </h1>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed max-w-3xl mb-12">
              Expert articles, patient guides, and the latest updates in medical tourism and healthcare in Kerala, India.
            </p>
          </FadeIn>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 animate-pulse">
                  <div className="h-40 bg-slate-100 rounded-xl mb-4" />
                  <div className="h-4 bg-slate-100 rounded w-1/3 mb-3" />
                  <div className="h-6 bg-slate-100 rounded w-3/4 mb-3" />
                  <div className="h-4 bg-slate-100 rounded w-full mb-2" />
                  <div className="h-4 bg-slate-100 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <FadeIn>
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
                <Tag className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-lg">No blog posts yet.</p>
                <p className="text-slate-400 text-sm mt-1">Check back soon for medical tourism insights and guides.</p>
              </div>
            </FadeIn>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, i) => (
                <FadeIn key={post.id} delay={i * 80}>
                  <Link to={`/blog/${post.slug}`} className="block" data-testid={`blog-card-${i}`}>
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-hover shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                      {post.image_url ? (
                        <div className="h-44 overflow-hidden">
                          <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="h-44 bg-gradient-to-br from-[#e0f2fe] to-[#F1F5F9] flex items-center justify-center">
                          <Tag className="w-10 h-10 text-[#00A4A9]/30" />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <Badge variant="secondary" className="text-xs bg-[#e0f2fe] text-[#00A4A9]">{post.category}</Badge>
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                        </div>
                        <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-2 line-clamp-2">{post.title}</h3>
                        <p className="text-sm text-slate-500 line-clamp-2 mb-4">{post.excerpt}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <User className="w-3 h-3" /> {post.author}
                          </span>
                          <span className="text-sm text-[#00A4A9] font-medium flex items-center gap-1">
                            Read More <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </FadeIn>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
